/**
 * Copyright 2025 Beijing Volcano Engine Technology Co., Ltd. All Rights Reserved.
 * SPDX-license-identifier: BSD-3-Clause
 */

import { useState, useEffect, useRef } from 'react';
import { Badge, Empty } from '@arco-design/web-react';
import { useSelector } from 'react-redux';
import { RootState } from '@/store';
import SSEClient from '@/utils/sse';
import API_CONFIG from '@/config/api';
import { isMobile } from '@/utils/utils';
import styles from './EmotionWarnings.module.less';

interface EmotionWarning {
  id: string;
  type: 'warning' | 'danger' | 'info';
  message: string;
  timestamp: string;
}

interface EmotionWarningsProps {
  onConnectionChange?: (connected: boolean) => void;
}

/**
 * SSE 连接管理组件
 * 
 * SSE 创建流程：
 * 1. 用户登录后（isAuthenticated = true）
 * 2. 用户点击"开启新会话"，加入房间成功后（isJoined = true）
 * 3. 创建 SSE 连接，Authorization 使用 apiKey
 * 4. 监听服务器数据，通过全局事件传递给 EmotionAnalysis
 * 5. 退出对话时（isJoined = false）关闭 SSE 连接
 */
function EmotionWarnings({ onConnectionChange }: EmotionWarningsProps) {
  const [warnings, setWarnings] = useState<EmotionWarning[]>([]);
  const [connected, setConnected] = useState(false);
  const sseClientRef = useRef<SSEClient | null>(null);
  
  // 从 Redux 获取状态
  const auth = useSelector((state: RootState) => state.auth);
  const room = useSelector((state: RootState) => state.room);
  const { isAuthenticated, apiKey } = auth;
  const { isJoined } = room;
  
  // 使用 useRef 缓存 isMobileDevice
  const isMobileDeviceRef = useRef(isMobile());
  const isMobileDevice = isMobileDeviceRef.current;

  /**
   * 核心 SSE 连接管理
   * 
   * 触发条件：
   * - isAuthenticated 变化（登录/退出登录）
   * - isJoined 变化（加入/退出对话）
   * - apiKey 变化
   * 
   * 创建条件：isAuthenticated && isJoined && apiKey
   * 关闭条件：!isAuthenticated || !isJoined
   */
  useEffect(() => {
    // 检查是否应该创建连接
    const shouldConnect = isAuthenticated && isJoined && apiKey;
    
    console.log('%c━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━', 'color: #13c2c2');
    console.log('%c🔍 [EmotionWarnings] SSE 连接状态检查', 'color: #13c2c2; font-weight: bold; font-size: 14px');
    console.log('%c━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━', 'color: #13c2c2');
    console.log('isAuthenticated:', isAuthenticated);
    console.log('isJoined:', isJoined);
    console.log('apiKey:', apiKey ? `${apiKey.substring(0, 20)}...` : '(未设置)');
    console.log('shouldConnect:', shouldConnect);
    console.log('当前连接状态:', sseClientRef.current ? '已连接' : '未连接');
    console.log('时间:', new Date().toLocaleTimeString());
    console.log('%c━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━', 'color: #13c2c2');
    
    // 如果不应该连接，关闭现有连接
    if (!shouldConnect) {
      if (sseClientRef.current) {
        console.log('%c🔌 [EmotionWarnings] 关闭 SSE 连接（退出对话或未登录）', 'color: #ff4d4f; font-weight: bold');
        try {
          sseClientRef.current.close();
          sseClientRef.current = null;
        } catch (error) {
          console.warn('关闭连接时出错:', error);
        }
        setConnected(false);
        // 更新全局状态
        (window as any).__sseConnected = false;
        onConnectionChange?.(false);
        window.dispatchEvent(new CustomEvent('sseConnectionChange', { detail: { connected: false } }));
      }
      return;
    }
    
    // 如果已有连接，不重复创建
    if (sseClientRef.current) {
      console.log('%c✅ [EmotionWarnings] SSE 连接已存在，无需重复创建', 'color: #52c41a; font-weight: bold');
      return;
    }
    
    // 创建新的 SSE 连接
    console.log('%c━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━', 'color: #52c41a');
    console.log('%c🚀 [EmotionWarnings] 开启对话成功，创建 SSE 连接', 'color: #52c41a; font-weight: bold; font-size: 14px');
    console.log('%c━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━', 'color: #52c41a');
    console.log('Authorization:', `${apiKey.substring(0, 20)}...`);
    console.log('设备类型:', isMobileDevice ? '移动端' : 'PC端');
    console.log('时间:', new Date().toLocaleTimeString());
    console.log('%c━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━', 'color: #52c41a');
    
    // 构建自定义 headers
    const customHeaders: Record<string, string> = {
      'Authorization': apiKey,
    };
    
    // 创建 SSE 连接
    const sseClient = new SSEClient({
      url: `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.EMOTION_WARNINGS_SSE}`,
      headers: customHeaders,
      onOpen: () => {
        console.log('%c━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━', 'color: #52c41a');
        console.log('%c✅ [EmotionWarnings] SSE 连接成功', 'color: #52c41a; font-weight: bold; font-size: 14px');
        console.log('%c━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━', 'color: #52c41a');
        
        setConnected(true);
        // 设置全局状态，供其他组件（如 EmotionAnalysis）读取
        (window as any).__sseConnected = true;
        onConnectionChange?.(true);
        window.dispatchEvent(new CustomEvent('sseConnectionChange', { detail: { connected: true } }));
      },
      onMessage: (data) => {
        console.log('%c━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━', 'color: #722ed1');
        console.log('%c📨 [EmotionWarnings] 收到 SSE 数据', 'color: #722ed1; font-weight: bold; font-size: 14px');
        console.log('%c━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━', 'color: #722ed1');
        console.log('数据:', data);
        console.log('message_type:', data.message_type);
        
        // 根据 message_type 分发不同类型的数据
        if (data.message_type === 'chat_topic') {
          // 话题列表消息 - 触发话题更新事件
          console.log('%c📋 [EmotionWarnings] 收到话题列表数据', 'color: #1890ff; font-weight: bold');
          window.dispatchEvent(new CustomEvent('chatTopicUpdate', {
            detail: data
          }));
        } else if (data.message_type === 'emotion_warning') {
          // 情绪预警消息 - 保持原有逻辑
          console.log('%c⚠️ [EmotionWarnings] 收到情绪预警数据', 'color: #faad14; font-weight: bold');
          
          // 触发全局事件，将数据传递给 EmotionAnalysis
          window.dispatchEvent(new CustomEvent('emotionDataUpdate', {
            detail: data
          }));
          
          // 处理预警数据
          const newWarning: EmotionWarning = {
            id: `${Date.now()}-${Math.random()}`,
            type: data.data?.warning_type === '无' ? 'info' : 'warning',
            message: data.data?.current_mood || JSON.stringify(data),
            timestamp: new Date().toLocaleTimeString('zh-CN'),
          };
          
          setWarnings((prev) => [newWarning, ...prev].slice(0, 10));
        } else {
          // 未知类型 - 按原有逻辑处理（兼容旧数据）
          console.log('%c❓ [EmotionWarnings] 收到未知类型数据，按情绪预警处理', 'color: #d9d9d9; font-weight: bold');
          
          window.dispatchEvent(new CustomEvent('emotionDataUpdate', {
            detail: data
          }));
          
          const newWarning: EmotionWarning = {
            id: `${Date.now()}-${Math.random()}`,
            type: data.type || 'info',
            message: data.message || data.current_mood || JSON.stringify(data),
            timestamp: new Date().toLocaleTimeString('zh-CN'),
          };
          
          setWarnings((prev) => [newWarning, ...prev].slice(0, 10));
        }
        
        console.log('%c━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━', 'color: #722ed1');
      },
      onError: (error) => {
        console.log('%c━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━', 'color: #ff4d4f');
        console.log('%c❌ [EmotionWarnings] SSE 连接错误', 'color: #ff4d4f; font-weight: bold; font-size: 14px');
        console.log('%c━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━', 'color: #ff4d4f');
        console.error(error);
        
        setConnected(false);
        // 更新全局状态
        (window as any).__sseConnected = false;
        onConnectionChange?.(false);
        window.dispatchEvent(new CustomEvent('sseConnectionChange', { detail: { connected: false } }));
      },
      reconnectInterval: 5000,
      maxReconnectAttempts: 10,
    });

    sseClient.connect();
    sseClientRef.current = sseClient;

    // 清理函数：组件卸载时关闭连接
    return () => {
      console.log('%c🔌 [EmotionWarnings] 组件卸载，关闭 SSE 连接', 'color: #d9d9d9; font-weight: bold');
      if (sseClientRef.current) {
        try {
          sseClientRef.current.close();
          sseClientRef.current = null;
        } catch (error) {
          console.warn('关闭连接时出错:', error);
        }
      }
    };
  }, [isAuthenticated, isJoined, apiKey, isMobileDevice, onConnectionChange]);

  // 组件挂载/卸载日志
  useEffect(() => {
    console.log('🟢 [EmotionWarnings] 组件挂载');
    return () => {
      console.log('🔴 [EmotionWarnings] 组件卸载');
    };
  }, []);
  
  // 移动端适配：监听页面可见性变化
  useEffect(() => {
    if (!isMobileDevice) return;
    
    const handleVisibilityChange = () => {
      if (document.hidden) {
        console.log('%c📱 [移动端] 页面切换到后台', 'color: #faad14; font-weight: bold');
      } else {
        console.log('%c📱 [移动端] 页面切换到前台', 'color: #52c41a; font-weight: bold');
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [isMobileDevice]);

  const getWarningColor = (type: string) => {
    switch (type) {
      case 'danger':
        return '#ff4d4f';
      case 'warning':
        return '#faad14';
      default:
        return '#00d4ff';
    }
  };

  const getWarningIcon = (type: string) => {
    switch (type) {
      case 'danger':
        return '⚠️';
      case 'warning':
        return '⚡';
      default:
        return '💡';
    }
  };

  return (
    <div className={styles.emotionWarnings}>
      <div className={styles.header}>
        <div className={styles.title}>情感预警</div>
        <Badge
          status={connected ? 'success' : 'error'}
          text={connected ? '已连接' : '未连接'}
          className={styles.statusBadge}
        />
      </div>

      <div className={styles.warningsList}>
        {warnings.length === 0 ? (
          <Empty
            description={connected ? '暂无预警信息' : '等待连接...'}
            style={{ marginTop: '20px' }}
          />
        ) : (
          warnings.map((warning) => (
            <div
              key={warning.id}
              className={styles.warningItem}
              style={{ borderLeftColor: getWarningColor(warning.type) }}
            >
              <div className={styles.warningHeader}>
                <span className={styles.warningIcon}>
                  {getWarningIcon(warning.type)}
                </span>
                <span className={styles.warningTime}>{warning.timestamp}</span>
              </div>
              <div className={styles.warningMessage}>{warning.message}</div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

export default EmotionWarnings;
